import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as QueryKeys from "data";

import { CreditUse, PlanInfo, ProductIcon } from "data/models";
import { fetchPlans } from "data/queries";
import { CircleCheck, Gem, Info, PlusCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

const getIcon = (iconType: ProductIcon, theme: Theme) => {
  if (iconType === "info") {
    return <Info style={{ color: theme.palette.info.main }} />;
  } else if (iconType == "check") {
    return <CircleCheck style={{ color: theme.palette.success.main }} />;
  } else if (iconType == "plus") {
    return <PlusCircle style={{ color: theme.palette.success.main }} />;
  } else if (iconType == "gem") {
    return <Gem style={{ color: theme.palette.info.main }} />;
  }
};

interface CreditUseModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CreditUseModal({ open, onClose }: CreditUseModalProps) {
  const theme = useTheme();
  const [categories, setCategories] = useState<Set<string>>(new Set());
  const plansQuery = useQuery({
    queryKey: [QueryKeys.plans],
    queryFn: () => fetchPlans(),
  });

  useEffect(() => {
    if (plansQuery.isSuccess && plansQuery.data) {
      const unique = new Set<string>();
      plansQuery.data.creditUse.forEach((creditUse: CreditUse) => {
        unique.add(creditUse.category);
      });
      setCategories(unique);
    }
  }, [plansQuery.isSuccess]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          backgroundImage: "none",
          p: 0,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>Feature Costs</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 12 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ px: 2, py: 0, pb: 2 }}>
        {plansQuery.isError && (
          <Alert severity="error">
            An error occurred while fetching the credit use.
          </Alert>
        )}
        {plansQuery.isPending && <Skeleton height={225} />}
        {plansQuery.isSuccess && (
          <Box>
            <Grid
              container
              spacing={0}
              sx={{
                fontWeight: 600,
                "& .MuiGrid-item": {
                  pl: 2,
                },
              }}
            >
              <Grid item xs={4}>
                {" "}
              </Grid>
              <Grid item xs={2}>
                Premium Data
              </Grid>
              <Grid item xs={4}>
                Method
              </Grid>
              <Grid item xs={2}>
                Credits
              </Grid>
              <Grid xs={12} mt={1} mb={1}>
                <Divider />
              </Grid>
            </Grid>
            {Array.from(categories).map((category, i) => (
              <React.Fragment key={i}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, my: 0.5, pl: 2 }}
                >
                  {category}
                </Typography>
                {plansQuery.data.creditUse
                  .filter((d) => d.category === category)
                  .map((entry, j) => (
                    <React.Fragment key={j}>
                      {entry.items.map((item, k) => (
                        <Grid
                          container
                          spacing={1}
                          key={k}
                          alignItems="center"
                          mt={0}
                          pl={2}
                          sx={{
                            "& .MuiGrid-item": {
                              pl: 0,
                            },
                            ml: 0,
                          }}
                        >
                          <Grid item xs={4}>
                            {k == 0 && (
                              <Typography variant="body2">
                                {entry.product}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={2}>
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              height="100%"
                            >
                              {item.premiumData && (
                                <Tooltip
                                  title={item.description || ""}
                                  placement="top"
                                >
                                  <Box display="flex" justifyContent="center">
                                    {getIcon(item.premiumData, theme)}
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            {item.method}
                          </Grid>
                          <Grid item xs={2}>
                            {item.credits}
                          </Grid>
                        </Grid>
                      ))}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
